import { useCallback, useEffect, useRef } from 'react';

const STEP_KEY = 'wizardStep';

type WizardHistoryState<T> = {
  ns: string;
  [STEP_KEY]: T;
};

/**
 * Syncs wizard step changes with browser history so the back button goes one step at a time.
 * Call `goBack()` from UI back buttons; use `restoreStep` to reset fields when landing on a step.
 */
export function useWizardBackNav<T extends string>(
  step: T,
  restoreStep: (targetStep: T) => void,
  namespace: string,
  enabled: boolean,
  initialStep: T,
) {
  const skipNextPush = useRef(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (!enabled) {
      initialized.current = false;
      return;
    }

    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as WizardHistoryState<T> | null;
      if (state?.ns === namespace && state[STEP_KEY] !== undefined) {
        skipNextPush.current = true;
        restoreStep(state[STEP_KEY]);
        return;
      }

      if (initialized.current) {
        skipNextPush.current = true;
        initialized.current = false;
        restoreStep(initialStep);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [enabled, namespace, restoreStep, initialStep]);

  useEffect(() => {
    if (!enabled) return;

    const historyState: WizardHistoryState<T> = { ns: namespace, [STEP_KEY]: step };

    if (!initialized.current) {
      initialized.current = true;
      window.history.replaceState(historyState, '');
      return;
    }

    if (skipNextPush.current) {
      skipNextPush.current = false;
      return;
    }

    window.history.pushState(historyState, '');
  }, [step, enabled, namespace]);

  const goBack = useCallback(() => {
    if (!enabled) return;
    window.history.back();
  }, [enabled]);

  const replaceStepInHistory = useCallback(
    (historyStep: T) => {
      if (!enabled) return;
      skipNextPush.current = true;
      window.history.replaceState({ ns: namespace, [STEP_KEY]: historyStep }, '');
    },
    [enabled, namespace],
  );

  return { goBack, replaceStepInHistory };
}
